import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import ReCAPTCHA from "react-google-recaptcha";
import axios from "axios";
import { prodApi } from "../data/api";
import { customColour } from "../data/colour";
import { useResponsiveFontSize } from "../functions";

const BookMeeting = ({
  siteKey,
  id,
  title,
  width = "80%",
  maxWidth = "100%",
  fontSize = "3rem",
  fontWeight = "100",
  gridTemplateColumns = "1fr 1fr",
  gap = "20px",
  buttonWidth = "100%",
  textFieldFontWeight = "100",
  textFieldMargin = "1rem",
  textFieldFontFamily = "'Ubuntu', sans-serif",
  dropdownBackground = "rgba(248, 248, 255, 0.9)",
  backgroundImage,
  background = `linear-gradient(
              to right bottom,
              rgba(0, 0, 0, 0.9),
              rgba(0, 0, 0, 0.7)
              )`,
  hoveredBackground = `linear-gradient(
                  to right bottom,
                  rgba(55, 166, 222, 0.75),
                  rgba(55, 166, 222, 0.375)
                  )`,
}) => {
  const [formValues, setFormValues] = useState({
    firstName: "",
    lastName: "",
    phoneNumber: "",
    email: "",
    preferred: "",
    summary: "",
  });

  const textFieldFontSize = useResponsiveFontSize();

  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionStatus, setSubmissionStatus] = useState(null);

  const reasonOptions = ["Phone Calls", "Texts", "Emails", "Zoom"];

  const handleChange = (e) => {
    setFormValues({
      ...formValues,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!captchaVerified) {
      alert("Please complete the CAPTCHA to verify that you are not a robot.");
      return;
    }

    setIsSubmitting(true);

    try {
      // Send form data to the back-end server using axios
      const response = await axios.post(prodApi.bookings, formValues);

      if (response.status === 200) {
        setSubmissionStatus("success");

        // Reset form fields on successful submission
        setFormValues({
          firstName: "",
          lastName: "",
          phoneNumber: "",
          email: "",
          preferred: "",
          summary: "",
        });

        // Refresh the page after a delay of 2 seconds
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else {
        setSubmissionStatus("failure");
        console.error("Submission failed:", response.data.message);
        // Refresh the page after a delay of 2 seconds on failure as well
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      }
    } catch (error) {
      setSubmissionStatus("failure");
      console.error("An error occurred:", error);

      // Refresh the page after a delay of 2 seconds on error
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCaptchaChange = (value) => {
    if (value) {
      setCaptchaVerified(true);
    }
  };

  return (
    <Box
      id={id}
      sx={{
        width: width,
        maxWidth,
        margin: "auto",
        padding: "1.5rem",
        background: backgroundImage,
        backdropFilter: "blur(1rem)",
        borderRadius: "8px",
        boxShadow: "0 0 1rem rgba(0, 0, 0, 0.2)",
        marginTop: "5rem",
      }}
    >
      <h1
        style={{
          fontSize: fontSize,
          margin: 0,
          fontWeight,
          textAlign: "center",
          marginBottom: "1rem",
        }}
      >
        {title}
      </h1>
      <form onSubmit={handleSubmit}>
        {/* First and Last Name Fields */}
        <Box sx={{ display: "grid", gridTemplateColumns, gap: gap }}>
          <TextField
            label="First Name"
            name="firstName"
            value={formValues.firstName}
            onChange={handleChange}
            fullWidth
            margin="normal"
            required
            InputProps={{
              sx: {
                fontSize: textFieldFontSize,
                fontWeight: textFieldFontWeight,
                fontFamily: textFieldFontFamily,
              },
            }}
            InputLabelProps={{
              sx: {
                fontSize: textFieldFontSize,
                fontWeight: textFieldFontWeight,
                fontFamily: textFieldFontFamily,
              },
            }}
            sx={{
              marginBottom: textFieldMargin,
              marginTop: textFieldMargin,
            }}
          />
          <TextField
            label="Last Name"
            name="lastName"
            value={formValues.lastName}
            onChange={handleChange}
            fullWidth
            margin="normal"
            InputProps={{
              sx: {
                fontSize: textFieldFontSize,
                fontWeight: textFieldFontWeight,
                fontFamily: textFieldFontFamily,
              },
            }}
            InputLabelProps={{
              sx: {
                fontSize: textFieldFontSize,
                fontWeight: textFieldFontWeight,
                fontFamily: textFieldFontFamily,
              },
            }}
            sx={{
              marginBottom: textFieldMargin,
              marginTop: textFieldMargin,
            }}
          />
        </Box>

        {/* Email and Phone Number Fields */}
        <Box sx={{ display: "grid", gridTemplateColumns, gap: gap }}>
          <TextField
            label="Email"
            name="email"
            type="email"
            value={formValues.email}
            onChange={handleChange}
            fullWidth
            margin="normal"
            required
            InputProps={{
              sx: {
                fontSize: textFieldFontSize,
                fontWeight: textFieldFontWeight,
                fontFamily: textFieldFontFamily,
              },
            }}
            InputLabelProps={{
              sx: {
                fontSize: textFieldFontSize,
                fontWeight: textFieldFontWeight,
                fontFamily: textFieldFontFamily,
              },
            }}
            sx={{
              marginBottom: textFieldMargin,
              marginTop: textFieldMargin,
            }}
          />
          <TextField
            label="Phone Number"
            name="phoneNumber"
            value={formValues.phoneNumber}
            onChange={handleChange}
            fullWidth
            margin="normal"
            required
            InputProps={{
              sx: {
                fontSize: textFieldFontSize,
                fontWeight: textFieldFontWeight,
                fontFamily: textFieldFontFamily,
              },
            }}
            InputLabelProps={{
              sx: {
                fontSize: textFieldFontSize,
                fontWeight: textFieldFontWeight,
                fontFamily: textFieldFontFamily,
              },
            }}
            sx={{
              marginBottom: textFieldMargin,
              marginTop: textFieldMargin,
            }}
          />
        </Box>

        {/* Preferred Means of Communication Dropdown */}
        <TextField
          select
          label="Communication Preference"
          name="preferred"
          value={formValues.preferred}
          onChange={handleChange}
          fullWidth
          margin="normal"
          required
          InputProps={{
            sx: {
              fontSize: textFieldFontSize,
              fontWeight: textFieldFontWeight,
              fontFamily: textFieldFontFamily,
            },
          }}
          InputLabelProps={{
            sx: {
              fontSize: textFieldFontSize,
              fontWeight: textFieldFontWeight,
              fontFamily: textFieldFontFamily,
            },
          }}
          sx={{
            marginBottom: textFieldMargin,
            marginTop: textFieldMargin,
          }}
          SelectProps={{
            MenuProps: {
              PaperProps: {
                sx: {
                  backgroundColor: dropdownBackground, // Change the dropdown background
                  "& .MuiMenuItem-root": {
                    fontSize: textFieldFontSize, // Set font size for each dropdown item
                    fontFamily: textFieldFontFamily, // Set font family for each dropdown item
                  },
                },
              },
            },
          }}
        >
          {reasonOptions.map((option, index) => (
            <MenuItem
              key={index}
              value={option}
              sx={{
                fontSize: textFieldFontSize,
                fontFamily: textFieldFontFamily,
              }}
            >
              {option}
            </MenuItem>
          ))}
        </TextField>

        {/* Summary Field */}
        <TextField
          label="Summary"
          name="summary"
          value={formValues.summary}
          onChange={handleChange}
          multiline
          rows={4}
          fullWidth
          required
          InputProps={{
            sx: {
              fontSize: textFieldFontSize,
              fontWeight: textFieldFontWeight,
              fontFamily: textFieldFontFamily,
            },
          }}
          InputLabelProps={{
            sx: {
              fontSize: textFieldFontSize,
              fontWeight: textFieldFontWeight,
              fontFamily: textFieldFontFamily,
            },
          }}
          sx={{
            marginBottom: textFieldMargin,
            marginTop: textFieldMargin,
          }}
        />

        {/* reCAPTCHA Component */}
        <Box
          sx={{ display: "flex", justifyContent: "center", margin: "2rem 0" }}
        >
          <ReCAPTCHA
            sitekey={siteKey} // Replace with your actual site key
            onChange={handleCaptchaChange}
          />
        </Box>

        {/* Submit Button */}
        <Button
          type="submit"
          sx={{
            width: buttonWidth,
            background: background,
            backdropFilter: "blur(1rem)",
            borderRadius: "0.25rem",
            color: "white",
            textDecoration: "none",
            "&:hover": {
              background: hoveredBackground,
              boxShadow: "0px 0px 20px rgba(55, 166, 222, 0.8)",
              filter: "brightness(1.2)",
            },
            marginTop: "2rem",
            paddingTop: "1rem",
            paddingBottom: "1rem",
            display: "grid",
            placeItems: "center",
            fontWeight: "100",
          }}
        >
          {isSubmitting ? (
            <CircularProgress size={24} sx={{ color: customColour.white }} />
          ) : (
            "Submit"
          )}
        </Button>

        {/* Submission Feedback */}
        {submissionStatus && (
          <Box
            sx={{
              textAlign: "center",
              marginTop: "1rem",
              color:
                submissionStatus === "success"
                  ? customColour.green
                  : customColour.warning,
              fontWeight: textFieldFontWeight,
              fontSize: textFieldFontSize,
            }}
          >
            {submissionStatus === "success"
              ? "Congratulations! Your inquiry has been submitted successfully."
              : "Something went wrong. Please try again later."}
          </Box>
        )}
      </form>
    </Box>
  );
};

export default BookMeeting;
